import { buildQuotedHtml } from './mailHubUtils';
import { EmailHtmlFrame } from './EmailHtmlFrame';
import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertCircle, AlertTriangle, Briefcase, Building, Check, CheckCircle,
  ChevronLeft, ChevronRight, Download, ExternalLink, Eye, EyeOff, FileText,
  Forward, Link2, Mail, MoreVertical, Paperclip, Reply, Save, Star, Trash2, User, LifeBuoy, UserCog,
  FolderInput, Folder, FolderMinus
} from 'lucide-react';

const MailEmailDetailPanel = ({
  currentFolder, deleteEmail, downloadAttachment, formatDateParis, handleCRMLink,
  hasNextEmail, hasPreviousEmail, markAsSpam, navigateEmail, openAttachmentInNewTab,
  openComposeModal, previewAttachment, processEmail, selectedEmail,
  setAttachmentToSave, setComposeForm, setEmailToLink, setReplyMode, setShowComposeModal,
  setShowEmailLinkManager, setShowEmailMenu, setShowSaveAttachmentModal, showEmailMenu,
  toggleImportant, trackingStats, unmarkSpam, onBackToList,
  emailFolders, moveEmailToFolder
}) => {
  const { t } = useTranslation('admin');
  const bodyScrollRef = useRef(null);
  const [showFolderSub, setShowFolderSub] = React.useState(false);

  // Remonter automatiquement au début du corps quand on change d'email
  useEffect(() => {
    if (bodyScrollRef.current) bodyScrollRef.current.scrollTop = 0;
    setShowFolderSub(false);
  }, [selectedEmail?.id]);

  return (
          <div data-testid="mail-detail-panel" className={`${selectedEmail ? 'flex' : 'hidden lg:flex'} lg:col-span-7 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex-col min-h-0`}>
            {selectedEmail ? (
              <>
                {/* Bouton retour liste (mobile/tablette uniquement) */}
                <button
                  data-testid="mail-detail-back"
                  onClick={onBackToList}
                  className="lg:hidden flex items-center gap-2 px-4 py-3 text-sm font-medium text-slate-600 hover:bg-slate-50 border-b border-slate-200 transition-colors"
                >
                  <ChevronLeft size={18} />
                  {t('mail.backToList', 'Retour à la liste')}
                </button>
                {/* Email Header */}
                <div className="p-6 border-b border-slate-200">
                  <div className="flex items-start justify-between mb-4">
                    <h3 data-testid="mail-detail-subject" className="text-xl font-bold text-slate-900 flex-1">{selectedEmail.subject}</h3>
                    
                    <div className="flex items-center space-x-2">
                      {/* Boutons Navigation Précédent/Suivant */}
                      <div className="flex items-center space-x-1 mr-2 border-r border-slate-200 pr-3">
                        <button
                          data-testid="mail-detail-prev"
                          onClick={() => navigateEmail('prev')}
                          disabled={!hasPreviousEmail()}
                          className={`p-2 rounded-lg transition-colors ${
                            hasPreviousEmail() 
                              ? 'hover:bg-slate-100 text-slate-600' 
                              : 'text-slate-300 cursor-not-allowed'
                          }`}
                          title={t('mail.prevEmail')}
                        >
                          <ChevronLeft size={20} />
                        </button>
                        <button
                          data-testid="mail-detail-next"
                          onClick={() => navigateEmail('next')}
                          disabled={!hasNextEmail()}
                          className={`p-2 rounded-lg transition-colors ${
                            hasNextEmail() 
                              ? 'hover:bg-slate-100 text-slate-600' 
                              : 'text-slate-300 cursor-not-allowed'
                          }`}
                          title={t('mail.nextEmail')}
                        >
                          <ChevronRight size={20} />
                        </button>
                      </div>
                      
                      {/* Badge CRM — visible si lié via linked_*_id ou linked_entities */}
                      {(selectedEmail.linked_client_id || selectedEmail.linked_courtier_id || selectedEmail.linked_company_id || selectedEmail.linked_assisteur_id || selectedEmail.linked_assistance_client_id || (selectedEmail.linked_entities && selectedEmail.linked_entities.length > 0)) && (
                        <button
                          onClick={() => handleCRMLink(selectedEmail)}
                          className={`px-3 py-1.5 rounded-lg transition-colors flex items-center space-x-2 text-sm font-medium ${
                            selectedEmail.linked_client_id
                              ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                              : selectedEmail.linked_courtier_id
                              ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                              : selectedEmail.linked_assisteur_id
                              ? 'bg-teal-100 text-teal-700 hover:bg-teal-200'
                              : selectedEmail.linked_assistance_client_id
                              ? 'bg-cyan-100 text-cyan-700 hover:bg-cyan-200'
                              : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                          }`}
                        >
                          {selectedEmail.linked_client_id ? <User size={14} /> :
                           selectedEmail.linked_courtier_id ? <Briefcase size={14} /> :
                           selectedEmail.linked_assisteur_id ? <LifeBuoy size={14} /> :
                           selectedEmail.linked_assistance_client_id ? <UserCog size={14} /> :
                           <Building size={14} />}
                          <span>{selectedEmail.linked_reference || selectedEmail.linked_name || t('mail.viewCrmCard')}</span>
                          <ExternalLink size={14} />
                        </button>
                      )}

                      {/* Bouton Classer — toujours visible */}
                      {selectedEmail.is_processed ? (
                        <span className="px-3 py-1.5 bg-gray-100 text-gray-500 rounded-lg text-sm font-medium flex items-center space-x-2">
                          <CheckCircle size={14} />
                          <span>{t('mail.processed')}</span>
                        </span>
                      ) : (
                        <button
                          onClick={() => {
                            const hasLink = !!(
                              selectedEmail.linked_client_id ||
                              selectedEmail.linked_courtier_id ||
                              selectedEmail.linked_company_id ||
                              selectedEmail.linked_assisteur_id ||
                              selectedEmail.linked_assistance_client_id ||
                              (selectedEmail.linked_entities && selectedEmail.linked_entities.length > 0)
                            );
                            if (!hasLink) {
                              toast.warning(t('mail.classerRequiresLink'));
                            } else {
                              processEmail(selectedEmail.id);
                            }
                          }}
                          data-testid="mail-classer-btn"
                          className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors flex items-center space-x-2 text-sm font-medium"
                          title={t('mail.processTitle')}
                        >
                          <CheckCircle size={14} />
                          <span>{t('mail.process')}</span>
                        </button>
                      )}
                      
                      <button
                        data-testid="mail-detail-important"
                        onClick={() => toggleImportant(selectedEmail.id, selectedEmail.is_important)}
                        className={`p-2 rounded-lg transition-all hover:scale-110 ${
                          selectedEmail.is_important 
                            ? 'text-amber-500 hover:bg-amber-50' 
                            : 'text-slate-400 hover:bg-slate-100 hover:text-amber-500'
                        }`}
                        title={selectedEmail.is_important ? t('mail.removeImportant') : t('mail.markImportant')}
                      >
                        <Star 
                          size={20} 
                          className={selectedEmail.is_important ? 'fill-amber-500' : ''} 
                        />
                      </button>
                      
                      <div className="relative email-menu-container">
                        <button data-testid="mail-detail-menu-toggle" className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowEmailMenu(!showEmailMenu);
                          }}
                        >
                          <MoreVertical size={18} />
                        </button>
                        
                        {/* Menu dropdown */}
                        {showEmailMenu && (
                          <div className="absolute right-0 top-10 bg-white border border-slate-200 rounded-lg shadow-xl z-50 py-1 min-w-[200px]">
                            {/* Répondre */}
                            <button
                              data-testid="mail-detail-reply"
                              onClick={() => {
                                setShowEmailMenu(false);
                                setReplyMode('reply');
                                setComposeForm(prev => ({
                                ...prev,
                                to: selectedEmail.from_address,
                                subject: `Re: ${selectedEmail.subject}`,
                                body: buildQuotedHtml(selectedEmail, `<p>${t('mail.replyQuoteHeader', { date: new Date(selectedEmail.received_at || selectedEmail.sent_at).toLocaleString('fr-FR'), author: selectedEmail.from_name || selectedEmail.from_address })}</p>`)
                              }));
                              setShowComposeModal(true);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-3"
                          >
                            <Reply size={16} />
                            {t('mail.reply')}
                          </button>
                          
                          {/* Transférer */}
                          <button
                            data-testid="mail-detail-forward"
                            onClick={() => {
                              setShowEmailMenu(false);
                              openComposeModal('forward', selectedEmail);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-3"
                          >
                            <Forward size={16} />
                            {t('mail.forward')}
                          </button>
                          
                          <div className="border-t border-slate-200 my-1"></div>
                          
                          {/* Lier au CRM */}
                          <button
                            data-testid="mail-detail-link"
                            onClick={() => {
                              setShowEmailMenu(false);
                              setEmailToLink(selectedEmail);
                              setShowEmailLinkManager(true);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-3"
                          >
                            <Link2 size={16} />
                            {t('mail.linkToContact')}
                          </button>

                          {/* Classer dans un dossier */}
                          <div className="border-t border-slate-200 my-1"></div>
                          <button
                            data-testid="mail-detail-move-folder"
                            onClick={() => setShowFolderSub(v => !v)}
                            className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-3"
                          >
                            <FolderInput size={16} />
                            <span className="flex-1">Classer dans un dossier</span>
                            <ChevronRight size={14} className={`transition-transform ${showFolderSub ? 'rotate-90' : ''}`} />
                          </button>
                          {showFolderSub && (
                            <div className="max-h-56 overflow-y-auto bg-slate-50 border-y border-slate-100">
                              {selectedEmail.folder_id && (
                                <button
                                  data-testid="mail-detail-folder-remove"
                                  onClick={() => { setShowEmailMenu(false); setShowFolderSub(false); moveEmailToFolder(selectedEmail.id, null); }}
                                  className="w-full pl-9 pr-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                >
                                  <FolderMinus size={15} /> Retirer du dossier
                                </button>
                              )}
                              {(emailFolders || []).length === 0 ? (
                                <p className="px-9 py-2 text-xs text-slate-400">Aucun dossier — créez-en un via « Dossiers ».</p>
                              ) : (
                                (emailFolders || []).map(folder => (
                                  <button
                                    key={folder.id}
                                    data-testid={`mail-detail-folder-${folder.id}`}
                                    disabled={selectedEmail.folder_id === folder.id}
                                    onClick={() => { setShowEmailMenu(false); setShowFolderSub(false); moveEmailToFolder(selectedEmail.id, folder.id); }}
                                    className={`w-full pl-9 pr-4 py-2 text-left text-sm flex items-center gap-2 ${selectedEmail.folder_id === folder.id ? 'text-slate-400 cursor-default' : 'text-slate-700 hover:bg-slate-100'}`}
                                  >
                                    <Folder size={15} className="text-amber-500" />
                                    <span className="truncate flex-1">{folder.name}</span>
                                    {selectedEmail.folder_id === folder.id && <Check size={13} className="text-green-600" />}
                                  </button>
                                ))
                              )}
                            </div>
                          )}
                          
                          {/* Classer */}
                          <button
                            onClick={() => {
                              setShowEmailMenu(false);
                              const hasLink = !!(
                                selectedEmail.linked_client_id ||
                                selectedEmail.linked_courtier_id ||
                                selectedEmail.linked_company_id ||
                                selectedEmail.linked_assisteur_id ||
                                selectedEmail.linked_assistance_client_id ||
                                (selectedEmail.linked_entities && selectedEmail.linked_entities.length > 0)
                              );
                              if (!hasLink) {
                                toast.warning(t('mail.classerRequiresLink'));
                              } else {
                                processEmail(selectedEmail.id);
                              }
                            }}
                            data-testid="mail-classer-menu-btn"
                            className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50 flex items-center gap-3"
                          >
                            <Check size={16} />
                            {t('mail.process')}
                          </button>
                          
                          <div className="border-t border-slate-200 my-1"></div>
                          
                          {/* Marquer comme important */}
                          <button
                            onClick={() => {
                              setShowEmailMenu(false);
                              toggleImportant(selectedEmail.id, selectedEmail.is_important);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-3"
                          >
                            <Star size={16} className={selectedEmail.is_important ? 'fill-amber-500 text-amber-500' : ''} />
                            {selectedEmail.is_important ? t('mail.removeImportant') : t('mail.markImportant')}
                          </button>
                          
                          {/* Spam */}
                          {currentFolder !== 'spam' ? (
                            <button
                              data-testid="mail-detail-spam"
                              onClick={() => {
                                setShowEmailMenu(false);
                                markAsSpam(selectedEmail.id);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-3"
                            >
                              <AlertTriangle size={16} />
                              {t('mail.markSpam')}
                            </button>
                          ) : (
                            <button
                              data-testid="mail-detail-unspam"
                              onClick={() => {
                                setShowEmailMenu(false);
                                unmarkSpam(selectedEmail.id);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-3"
                            >
                              <CheckCircle size={16} />
                              {t('mail.notSpam')}
                            </button>
                          )}
                          
                          <div className="border-t border-slate-200 my-1"></div>
                          
                          {/* Supprimer */}
                          <button
                            data-testid="mail-detail-delete"
                            onClick={() => {
                              setShowEmailMenu(false);
                              deleteEmail(selectedEmail.id, currentFolder === 'trash');
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3"
                          >
                            <Trash2 size={16} />
                            {currentFolder === 'trash' ? t('mail.deletePermanentTitle') : t('mail.moveToTrashMenu')}
                          </button>
                        </div>
                      )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center">
                      <span className="text-slate-600 font-medium w-24">{t('mail.fromLabel')}</span>
                      <span className="text-slate-900">
                        {selectedEmail.from_name ? `${selectedEmail.from_name} <${selectedEmail.from_address}>` : selectedEmail.from_address}
                      </span>
                    </div>
                    
                    <div className="flex items-center">
                      <span className="text-slate-600 font-medium w-24">{t('mail.toLabel')}</span>
                      <span className="text-slate-900">{selectedEmail.to_addresses.join(', ')}</span>
                    </div>
                    
                    {selectedEmail.cc_addresses?.length > 0 && (
                      <div className="flex items-center">
                        <span className="text-slate-600 font-medium w-24">{t('mail.ccLabel')}</span>
                        <span className="text-slate-900">{selectedEmail.cc_addresses.join(', ')}</span>
                      </div>
                    )}
                    
                    <div className="flex items-center">
                      <span className="text-slate-600 font-medium w-24">{t('mail.dateLabel')}</span>
                      <span className="text-slate-900">
                        {formatDateParis(selectedEmail.received_at || selectedEmail.sent_at, {
                          dateStyle: 'full',
                          timeStyle: 'short'
                        })}
                      </span>
                    </div>

                    {/* Tracking Stats (pour emails envoyés) */}
                    {selectedEmail.direction === 'outbound' && trackingStats && trackingStats.tracking_enabled && (
                      <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex items-center space-x-2">
                          {trackingStats.opened ? (
                            <>
                              <Eye size={16} className="text-green-600" />
                              <div className="flex-1">
                                <p className="text-sm font-semibold text-green-700">
                                  {t('mail.readByRecipient')}
                                </p>
                                <p className="text-xs text-slate-600 mt-1">
                                  {t('mail.firstOpen', { date: formatDateParis(trackingStats.first_opened_at, {
                                    dateStyle: 'medium',
                                    timeStyle: 'short'
                                  }) })}
                                </p>
                                {trackingStats.open_count > 1 && (
                                  <p className="text-xs text-slate-600">
                                    {t('mail.openedTimesLast', { count: trackingStats.open_count, date: formatDateParis(trackingStats.last_opened_at, {
                                      dateStyle: 'medium',
                                      timeStyle: 'short'
                                    }) })}
                                  </p>
                                )}
                              </div>
                            </>
                          ) : (
                            <>
                              <EyeOff size={16} className="text-slate-400" />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-slate-600">
                                  {t('mail.notYetRead')}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {t('mail.willBeNotified')}
                                </p>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2 mt-4 pt-4 border-t border-slate-200">
                    <button
                      onClick={() => openComposeModal('reply', selectedEmail)}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors flex items-center space-x-2 text-sm"
                    >
                      <Reply size={14} />
                      <span>{t('mail.reply')}</span>
                    </button>

                    <button
                      onClick={() => openComposeModal('forward', selectedEmail)}
                      className="px-4 py-2 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors flex items-center space-x-2 text-sm"
                    >
                      <Forward size={14} />
                      <span>{t('mail.forward')}</span>
                    </button>

                    <button
                      onClick={() => {
                        setEmailToLink(selectedEmail);
                        setShowEmailLinkManager(true);
                      }}
                      className="px-4 py-2 bg-blue-100 text-blue-700 rounded-xl hover:bg-blue-200 transition-colors flex items-center space-x-2 text-sm"
                      title={t('mail.linkTitle')}
                    >
                      <Link2 size={14} />
                      <span>{t('mail.linkShort')}</span>
                    </button>

                    {currentFolder === 'spam' ? (
                      <button 
                        onClick={() => unmarkSpam(selectedEmail.id)}
                        className="px-4 py-2 bg-green-100 text-green-700 rounded-xl hover:bg-green-200 transition-colors flex items-center space-x-2 text-sm"
                      >
                        <CheckCircle size={14} />
                        <span>{t('mail.unmarkSpam')}</span>
                      </button>
                    ) : (
                      <button 
                        onClick={() => markAsSpam(selectedEmail.id)}
                        className="px-4 py-2 bg-orange-100 text-orange-700 rounded-xl hover:bg-orange-200 transition-colors flex items-center space-x-2 text-sm"
                      >
                        <AlertCircle size={14} />
                        <span>{t('mail.spamShort')}</span>
                      </button>
                    )}

                    <button 
                      onClick={() => deleteEmail(selectedEmail.id, currentFolder === 'trash')}
                      className={`px-4 py-2 rounded-xl transition-colors flex items-center space-x-2 text-sm ${
                        currentFolder === 'trash'
                          ? 'bg-red-600 text-white hover:bg-red-700'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      <Trash2 size={14} />
                      <span>{currentFolder === 'trash' ? t('mail.deletePermanentTitle') : t('mail.deleteShort')}</span>
                    </button>
                  </div>
                </div>

                {/* Email Body */}
                <div ref={bodyScrollRef} className="flex-1 overflow-y-auto min-h-0 p-6">
                  {selectedEmail.body_html ? (
                    <EmailHtmlFrame html={selectedEmail.body_html} />
                  ) : (
                    <pre className="whitespace-pre-wrap font-sans text-slate-700">
                      {selectedEmail.body_text}
                    </pre>
                  )}

                  {/* Attachments */}
                  {selectedEmail.attachments?.length > 0 && (
                    <div data-testid="mail-attachments-section" className="mt-6 pt-6 border-t border-slate-200">
                      <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center">
                        <Paperclip size={16} className="mr-2" />
                        {t('mail.attachments', { count: selectedEmail.attachments.length })}
                      </h4>
                      <div className="space-y-2">
                        {selectedEmail.attachments.map((attachment, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200">
                            <div className="flex items-center">
                              <FileText size={20} className="text-slate-400 mr-3" />
                              <div>
                                <p className="text-sm font-medium text-slate-900">{attachment.original_filename}</p>
                                <p className="text-xs text-slate-500">{(attachment.file_size / 1024).toFixed(2)} KB</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-1">
                              <button 
                                data-testid={`mail-attachment-preview-${index}`}
                                onClick={() => previewAttachment(selectedEmail.id, index, attachment.original_filename)}
                                className="text-indigo-600 hover:text-indigo-800 p-2 hover:bg-indigo-50 rounded-lg transition-colors"
                                title={t('mail.preview')}
                              >
                                <Eye size={18} />
                              </button>
                              <button 
                                data-testid={`mail-attachment-open-${index}`}
                                onClick={() => openAttachmentInNewTab(selectedEmail.id, index, attachment.original_filename)}
                                className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded-lg transition-colors"
                                title={t('mail.openNewTab')}
                              >
                                <ExternalLink size={18} />
                              </button>
                              <button 
                                data-testid={`mail-attachment-download-${index}`}
                                onClick={() => downloadAttachment(selectedEmail.id, index, attachment.original_filename)}
                                className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded-lg transition-colors"
                                title={t('mail.download')}
                              >
                                <Download size={18} />
                              </button>
                              <button 
                                data-testid={`mail-attachment-save-${index}`}
                                onClick={() => {
                                  setAttachmentToSave({
                                    attachment,
                                    emailId: selectedEmail.id,
                                    attachmentIndex: index
                                  });
                                  setShowSaveAttachmentModal(true);
                                }}
                                className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition-colors"
                                title={t('mail.saveToCrm')}
                              >
                                <Save size={18} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div data-testid="mail-detail-empty" className="flex flex-col items-center justify-center h-full text-slate-400">
                <Mail size={64} className="mb-4" />
                <p className="text-lg">{t('mail.selectEmailToDisplay')}</p>
              </div>
            )}
          </div>
  );
};

export default MailEmailDetailPanel;
